import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import intl from 'components/i18n/ReactIntlWrapper';
import DynamicAutocomplete from 'components/common/DynamicAutocomplete';
import { ENTITY_TYPE } from 'common/constants';
import type { NotificationConfigDTO } from 'axiosApi/models/notification-config-dto';
import type { NotificationFESchemaDTO } from 'axiosApi/models/notification-feschema-dto';
import type { UserEntityTemplate } from 'axiosApi/models/user-entity-template';
import { Alert } from '@mui/material';

export interface NotificationConfigurationDrawerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (moduleName: string, actionName: string, config: NotificationConfigDTO) => void;
  onDiscard: (moduleName: string, actionName: string) => void;
  moduleName: string | null;
  actionName: string | null;
  schema: NotificationFESchemaDTO | null | undefined;
  config: NotificationConfigDTO | null | undefined;
  allEmployees: any[];
  allClients: any[];
  allProviders: any[];
  allCorporateEntities: any[];
  allScopes: any[];
  initialSelectedEmployees: any[];
  initialSelectedClients: any[];
  initialSelectedProviders: any[];
  initialSelectedCorporateEntities: any[];
  initialSelectedScopes: any[];
}

/**
 * NotificationConfigurationDrawer - Right-side drawer for configuring notification action settings
 *
 * Displays:
 * - Action name and description (from schema.message.resourceID)
 * - Priority dropdown (from schema.priority)
 * - Recipients section (if schema.recipients.allowEntities)
 *   - DynamicAutocomplete for Employee, Client, CorporateEntity, Provider
 * - Scopes dropdown (if schema.recipients.allowScopes)
 * - Supervisor dropdown (from schema.recipients.supervisor)
 *
 * Priority selection:
 * - Options from schema.priority.options
 * - Default: config.priority || schema.priority.default
 *
 * State Management:
 * - Confirm: Stores changes in pending state (no API call)
 * - Discard: Reverts to last confirmed state (or original if no pending changes)
 * - Changes are only persisted to API when "Save All" is clicked in parent component
 */
const NotificationConfigurationDrawer: React.FC<NotificationConfigurationDrawerProps> = ({
  open,
  onClose,
  onConfirm,
  onDiscard,
  moduleName,
  actionName,
  schema,
  config,
  allEmployees,
  allClients,
  allProviders,
  allCorporateEntities,
  allScopes,
  initialSelectedEmployees,
  initialSelectedClients,
  initialSelectedProviders,
  initialSelectedCorporateEntities,
  initialSelectedScopes
}) => {
  const [priority, setPriority] = useState<string>('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [supervisor, setSupervisor] = useState<string>('');

  const [employees, setEmployees] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [corporateEntities, setCorporateEntities] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);

  // Track initial values to detect changes
  const [initialState, setInitialState] = useState<{
    priority: string;
    scopes: string[];
    supervisor: string;
    employees: any[];
    clients: any[];
    corporateEntities: any[];
    providers: any[];
  } | null>(null);

  // Navigation dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<boolean>(false);

  // Initialize form when drawer opens or data changes
  useEffect(() => {
    
    if (open && schema) {
      // Set priority (handle both PascalCase and camelCase)
      const defaultPriority = (config as any)?.Priority || config?.priority || schema.priority?.default || 'medium';
      setPriority(defaultPriority);

      // Set selected entities from props (already filtered and matched by parent)
      setEmployees(initialSelectedEmployees);
      setClients(initialSelectedClients);
      setProviders(initialSelectedProviders);
      setCorporateEntities(initialSelectedCorporateEntities);

      // Set scopes from initialSelectedScopes (already filtered by parent)
      // Extract scope strings from ScopeDTO objects
      const scopeStrings = initialSelectedScopes.map((scopeObj) => scopeObj.scope || '');
      setScopes(scopeStrings);

      // Set supervisor (handle both PascalCase and camelCase)
      const recipients = (config as any)?.Recipients || config?.recipients;
      const defaultSupervisor = recipients?.Supervisor || recipients?.supervisor || 'none';
      setSupervisor(defaultSupervisor);

      // Store initial state for change detection
      setInitialState({
        priority: defaultPriority,
        scopes: scopeStrings, // Use the same scope strings format
        supervisor: defaultSupervisor,
        employees: initialSelectedEmployees,
        clients: initialSelectedClients,
        corporateEntities: initialSelectedCorporateEntities,
        providers: initialSelectedProviders,
      });
    }
  }, [
    open,
    schema,
    config,
    initialSelectedEmployees,
    initialSelectedClients,
    initialSelectedProviders,
    initialSelectedCorporateEntities,
    initialSelectedScopes
  ]);

  /**
   * Check if current form state differs from initial state
   */
  const hasUnsavedChanges = (): boolean => {
    if (!initialState) return false;

    // Compare primitive values
    if (priority !== initialState.priority) return true;
    if (supervisor !== initialState.supervisor) return true;

    // Compare arrays using JSON stringify (simple but effective)
    if (JSON.stringify(scopes) !== JSON.stringify(initialState.scopes)) return true;
    if (JSON.stringify(employees) !== JSON.stringify(initialState.employees)) return true;
    if (JSON.stringify(clients) !== JSON.stringify(initialState.clients)) return true;
    if (JSON.stringify(corporateEntities) !== JSON.stringify(initialState.corporateEntities)) return true;
    if (JSON.stringify(providers) !== JSON.stringify(initialState.providers)) return true;

    return false;
  };

  /**
   * Handle drawer close - check for unsaved changes first
   */
  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  /**
   * Handle unsaved changes dialog - Stay
   */
  const handleStayInDrawer = () => {
    setShowUnsavedDialog(false);
  };

  /**
   * Handle unsaved changes dialog - Leave
   */
  const handleLeaveDrawer = () => {
    setShowUnsavedDialog(false);
    if (!moduleName || !actionName) {
      onClose();
      return;
    }
    onDiscard(moduleName, actionName);
    onClose();
  };

  const handleConfirm = () => {
    if (!moduleName || !actionName) return;

    // Reconstruct the entities array from the four DynamicAutocomplete states
    // Using API model: UserEntityTemplate[]
    const entitiesArray: UserEntityTemplate[] = [
      ...employees.map((emp) => ({
        entityId: emp.id?.toString() || null,
        entityType: String(ENTITY_TYPE.EMPLOYEE),
      })),
      ...clients.map((client) => ({
        entityId: client.id?.toString() || null,
        entityType: String(ENTITY_TYPE.CLIENT),
      })),
      ...providers.map((provider) => ({
        entityId: provider.id?.toString() || null,
        entityType: String(ENTITY_TYPE.PROVIDER),
      })),
      ...corporateEntities.map((entity) => ({
        entityId: entity.id?.toString() || null,
        entityType: String(ENTITY_TYPE.CORPORATE_ENTITY),
      })),
    ];

    // Build config using API model: NotificationConfigDTO
    // Note: isActive is included as an extra property (not in TypeScript interface but expected by API)
    const newConfig: NotificationConfigDTO & { isActive?: boolean } = {
      priority: priority || null,
      module: moduleName,
      recipients: {
        entities: entitiesArray.length > 0 ? entitiesArray : null,
        scopes: schema?.recipients?.allowScopes && scopes.length > 0 ? scopes : null,
        supervisor: supervisor || null,
      },
      isActive: true, // When user confirms config, mark action as active
    };

    onConfirm(moduleName, actionName, newConfig);
    onClose();
  };

  const handleDiscard = () => {
    if (!moduleName || !actionName) return;
    
    onDiscard(moduleName, actionName);
    onClose();
  };

  // Don't render if no schema
  if (!schema) {
    return null;
  }

  const showRecipients = schema.recipients?.allowEntities === true;
  const showScopes = schema.recipients?.allowScopes === true;
  const supervisorOptions = schema.recipients?.supervisor || ['none'];

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: 550, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="h3" color="primary" sx={{ mt: 2 }}>
                  {intl.get('common.configuration')}
                </Typography>
                <Typography variant="h4" color="primary" sx={{ mt: 2 }}>
                  {intl.get(`${actionName}`)}
                </Typography>
                {schema.message?.resourceID && (
                  <>
                    <Typography variant="h6" color="text.primary" sx={{ mt: 2 }}>
                      {intl.get('notification.resourceID.displayed')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                      {`"${intl.get(schema.message.resourceID)}"`}
                    </Typography>
                  </>
                )}
              </Box>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

        <Divider />

        {/* Form Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={3}>
            {/* Priority Dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get('priority')}</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                label={intl.get('priority')}
              >
                {schema.priority?.options?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Helper Text */}
            <Alert severity="info" sx={{ mt: 2 }}>
              {intl.get('notification.configuration.helperText')}
            </Alert>

            {/* Recipients Section */}
            {showRecipients && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    {intl.get('common.recipients')}
                  </Typography>
                  <Stack spacing={2}>
                    {/* Employees */}
                    <FormControl fullWidth>
                      <DynamicAutocomplete
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} ${option.lastName || ''}`.trim()
                            : option?.label || ''
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setEmployees(newValue || [])}
                        options={allEmployees}
                        renderInput={(params) => (
                          <TextField {...params} label={intl.get('employee.header')} />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id.toString()}>
                            {option.name
                              ? `${option.name} ${option.lastName || ''}`.trim()
                              : option.label}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id}
                              label={
                                option.name
                                  ? `${option.name} ${option.lastName || ''}`.trim()
                                  : option.label
                              }
                              color="primary"
                              variant="outlined"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        value={employees}
                      />
                    </FormControl>

                    {/* Clients */}
                    <FormControl fullWidth>
                      <DynamicAutocomplete
                        getOptionLabel={(option) => option?.name || option?.label || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setClients(newValue || [])}
                        options={allClients}
                        renderInput={(params) => (
                          <TextField {...params} label={intl.get('clients.header')} />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id.toString()}>
                            {option.name || option.label}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id}
                              label={option.name || option.label}
                              color="primary"
                              variant="outlined"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        value={clients}
                      />
                    </FormControl>

                    {/* Providers */}
                    <FormControl fullWidth>
                      <DynamicAutocomplete
                        getOptionLabel={(option) => option?.name || option?.label || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setProviders(newValue || [])}
                        options={allProviders}
                        renderInput={(params) => (
                          <TextField {...params} label={intl.get('providers.header')} />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id.toString()}>
                            {option.name || option.label}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id}
                              label={option.name || option.label}
                              color="primary"
                              variant="outlined"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        value={providers}
                      />
                    </FormControl>

                    {/* Corporate Entities */}
                    <FormControl fullWidth>
                      <DynamicAutocomplete
                        getOptionLabel={(option) => option?.name || option?.label || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setCorporateEntities(newValue || [])}
                        options={allCorporateEntities}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={intl.get('mainNavigation.setting.corporateEntities')}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id.toString()}>
                            {option.name || option.label}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id}
                              label={option.name || option.label}
                              color="primary"
                              variant="outlined"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        value={corporateEntities}
                      />
                    </FormControl>
                  </Stack>
                </Box>
              </>
            )}

            {/* Scopes Dropdown */}
            {showScopes && (
              <>
                <Divider />
                <Autocomplete
                  multiple
                  size="small"
                  options={allScopes}
                  value={allScopes.filter((scopeObj) => scopes.includes(scopeObj.scope || ''))}
                  onChange={(_, newValue) => setScopes(newValue.map((scopeObj) => scopeObj.scope || ''))}
                  renderInput={(params) => <TextField {...params} label={intl.get('scopes')} />}
                  getOptionLabel={(option) => option.scope || ''}
                  isOptionEqualToValue={(option, value) => option.scope === value.scope}
                />
              </>
            )}

            {/* Supervisor Dropdown */}
            <Divider />
            <FormControl fullWidth size="small">
              <InputLabel>Supervisor</InputLabel>
              <Select
                value={supervisor}
                onChange={(e) => setSupervisor(e.target.value)}
                label="Supervisor"
              >
                {supervisorOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

          {/* Footer Actions */}
          <Divider />
          <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleDiscard} variant="outlined">
              {intl.get('common.discard') || 'Discard'}
            </Button>
            <Button onClick={handleConfirm} variant="contained">
              {intl.get('common.confirm') || 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleStayInDrawer}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          {intl.get('notification.configuration.unsavedChanges.title') || 'Unsaved Changes'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-changes-dialog-description">
            {intl.get('notification.configuration.unsavedChanges.message') || 
              'You have unsaved changes in this configuration. If you leave now, your changes will be lost. Are you sure you want to leave?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStayInDrawer} color="primary">
            {intl.get('notification.configuration.unsavedChanges.stay') || 'Stay'}
          </Button>
          <Button 
            onClick={handleLeaveDrawer} 
            color="error" 
            variant="contained" 
            autoFocus
          >
            {intl.get('notification.configuration.unsavedChanges.leave') || 'Leave'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationConfigurationDrawer;
